import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import DateInput from "../../components/form/DateInput";
import { getAnalyticsOverview } from "../../features/analyticsSlice";

// Mirrors SUPPORTED_ACQUISITION_RANGES on the backend.
const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "6m", label: "Last 6 Months" },
  { value: "custom", label: "Custom Date Range" },
];

const SECTION_ACCENTS = {
  acquisition: "#12a150",
  accountStatus: "#e8720c",
  activity: "#2f6fed",
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

const formatPercentage = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;

const sectionHeaderStyle = {
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 700,
};

const tileStyle = {
  borderRadius: "14px",
  border: "1px solid #e9edf4",
  height: "100%",
};

const tileIconStyle = (color, bgColor) => ({
  width: "52px",
  height: "52px",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: bgColor,
  color,
  fontSize: "20px",
});

const SectionHeader = ({ index, title, subtitle, accent }) => (
  <div className="d-flex align-items-center gap-2 mb-3">
    <span style={{ ...sectionHeaderStyle, backgroundColor: accent }}>{index}</span>
    <h6 className="mb-0 fw-bold text-uppercase" style={{ letterSpacing: "0.04em" }}>
      {title}
    </h6>
    {subtitle ? <small className="text-muted">({subtitle})</small> : null}
  </div>
);

const StatTile = ({ title, value, caption, icon, accent, bgColor, compact }) => (
  <div className="card shadow-none h-100" style={tileStyle}>
    <div className={`card-body text-center ${compact ? "p-3" : "p-4"}`}>
      <p className="text-muted mb-2" style={{ fontSize: compact ? "13px" : "14px" }}>
        {title}
      </p>
      {icon ? (
        <div className="d-flex justify-content-center mb-2">
          <span style={tileIconStyle(accent, bgColor)}>
            <i className={icon} />
          </span>
        </div>
      ) : null}
      <h3 className="fw-bold mb-1" style={{ color: accent }}>
        {value}
      </h3>
      {caption ? (
        <small className="text-muted d-block">{caption}</small>
      ) : null}
    </div>
  </div>
);

// Icons cycle per paid tier so each plan bucket still gets a distinct glyph when new
// plans are added on the backend.
const PAID_TIER_ICONS = ["bi bi-award-fill", "bi bi-gem", "bi bi-crown-fill"];

const bucketIcon = (bucket, paidIndex) => {
  if (bucket.key === "trial") return "bi bi-hourglass-split";
  if (bucket.key === "free") return "bi bi-person-fill";
  return PAID_TIER_ICONS[paidIndex % PAID_TIER_ICONS.length];
};

const Analytics = () => {
  const dispatch = useDispatch();
  const { isLoader, acquisition, accountStatus, activity, filter } = useSelector(
    (state) => state.analytics
  );

  const [range, setRange] = useState("today");
  const [customRange, setCustomRange] = useState({ fromDate: "", toDate: "" });

  useEffect(() => {
    if (range === "custom") {
      // The backend rejects a custom range without both ends, so hold off until the
      // admin has picked them.
      if (!customRange.fromDate || !customRange.toDate) {
        return;
      }

      dispatch(
        getAnalyticsOverview({
          range,
          fromDate: customRange.fromDate,
          toDate: customRange.toDate,
        })
      );
      return;
    }

    dispatch(getAnalyticsOverview({ range }));
  }, [dispatch, range, customRange.fromDate, customRange.toDate]);

  const breakdown = useMemo(
    () => (Array.isArray(accountStatus?.breakdown) ? accountStatus.breakdown : []),
    [accountStatus]
  );

  const acquisitionCards = [
    {
      title: "New Downloads",
      value: formatNumber(acquisition?.newInstalls),
      caption: "Total installs during period",
      icon: "bi bi-download",
    },
    {
      title: "New Registered Accounts",
      value: formatNumber(acquisition?.newRegisteredAccounts),
      caption: "Accounts created during period",
      icon: "bi bi-person-plus-fill",
    },
    {
      title: "Download-to-Registration Conversion Rate",
      value: formatPercentage(acquisition?.installToRegistrationConversionRate),
      caption: `${formatNumber(acquisition?.newRegisteredAccounts)} ÷ ${formatNumber(
        acquisition?.newInstalls
      )}`,
      icon: "bi bi-percent",
    },
  ];

  const activityCards = [
    { title: "Active Users", value: activity?.activeUsers, icon: "bi bi-person-fill" },
    { title: "Quests Created", value: activity?.questsCreated, icon: "bi bi-clipboard-plus-fill" },
    { title: "Quests Completed", value: activity?.questsCompleted, icon: "bi bi-check-circle-fill" },
    { title: "Hunts Created", value: activity?.huntsCreated, icon: "bi bi-flag-fill" },
    { title: "Hunts Joined", value: activity?.huntsJoined, icon: "bi bi-people-fill" },
    { title: "Hunts Completed", value: activity?.huntsCompleted, icon: "bi bi-bullseye" },
  ];

  let paidTierIndex = -1;

  return (
    <main id="main" className="main">
      <Breadcrumb page="Analytics" />

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <SectionHeader
            index="1"
            title="Reporting Period"
            subtitle="Applies to sections below"
            accent="#6f42c1"
          />

          <div className="d-flex flex-wrap gap-2">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`btn ${
                  range === option.value ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {range === "custom" ? (
            <div className="row g-3 mt-1">
              <div className="col-12 col-lg-4">
                <label className="form-label fw-semibold">From date</label>
                <DateInput
                  className="form-control"
                  value={customRange.fromDate}
                  onChange={(val) =>
                    setCustomRange((prev) => ({ ...prev, fromDate: val }))
                  }
                />
              </div>
              <div className="col-12 col-lg-4">
                <label className="form-label fw-semibold">To date</label>
                <DateInput
                  className="form-control"
                  value={customRange.toDate}
                  onChange={(val) =>
                    setCustomRange((prev) => ({ ...prev, toDate: val }))
                  }
                />
              </div>
            </div>
          ) : null}

          {filter ? (
            <small className="text-muted d-block mt-3">
              Showing {filter.fromDate} to {filter.toDate} ({filter.timezone})
            </small>
          ) : null}
        </div>
      </div>

      {isLoader ? (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : null}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <SectionHeader
            index="2"
            title="Acquisition"
            subtitle="During Selected Period"
            accent={SECTION_ACCENTS.acquisition}
          />

          <div className="row g-3">
            {acquisitionCards.map((card) => (
              <div className="col-12 col-md-4" key={card.title}>
                <StatTile
                  title={card.title}
                  value={card.value}
                  caption={card.caption}
                  icon={card.icon}
                  accent={SECTION_ACCENTS.acquisition}
                  bgColor="#e6f6ed"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <SectionHeader
            index="3"
            title="Account Status"
            subtitle="As of End of Selected Period"
            accent={SECTION_ACCENTS.accountStatus}
          />

          <div className="row g-3">
            <div className="col-12 col-md-4 col-xl-3">
              <StatTile
                title="Current Registered Accounts"
                value={formatNumber(accountStatus?.totalRegisteredAccounts)}
                caption="Total accounts"
                icon="bi bi-people-fill"
                accent={SECTION_ACCENTS.accountStatus}
                bgColor="#fdeedd"
              />
            </div>

            {breakdown.map((bucket) => {
              if (bucket.key !== "trial" && bucket.key !== "free") {
                paidTierIndex += 1;
              }

              return (
                <div className="col-6 col-md-4 col-xl-2" key={bucket.key}>
                  <StatTile
                    compact
                    title={bucket.label}
                    value={formatNumber(bucket.count)}
                    caption={formatPercentage(bucket.percentage)}
                    icon={bucketIcon(bucket, Math.max(paidTierIndex, 0))}
                    accent={SECTION_ACCENTS.accountStatus}
                    bgColor="#fdeedd"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <SectionHeader
            index="4"
            title="Activity"
            subtitle="During Selected Period"
            accent={SECTION_ACCENTS.activity}
          />

          <div className="row g-3">
            {activityCards.map((card) => (
              <div className="col-6 col-md-4 col-xl-2" key={card.title}>
                <StatTile
                  compact
                  title={card.title}
                  value={formatNumber(card.value)}
                  icon={card.icon}
                  accent={SECTION_ACCENTS.activity}
                  bgColor="#e7effd"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Analytics;
